import {
  Component,
  ChangeDetectionStrategy,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';

import { RegistrationsComponent } from '@dpr-details/models/component';
import {
  DprDetailsGridBaseComponent,
  DprHseDetailGridData,
} from '@dpr-details/models/grid';
import { NumericEditComponent, TextDisplayComponent } from '@grid/components';
import { DprHseGridService } from '@dpr-details/services/grid';
import { GridAlignment, GridCellContentAlignment } from '@grid/enums';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';
import { GridColumn, GridOptions } from '@grid/models';
import { Constants, getStaticMessage } from '@shared/utils';
import { StaticMessage } from '@shared/enums';

@UntilDestroy()
@Component({
  selector: 'app-dpr-hse-registrations-grid',
  templateUrl: './dpr-hse-registrations-grid.component.html',
  styleUrls: ['./dpr-hse-registrations-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprHseRegistrationsGridComponent
  extends DprDetailsGridBaseComponent<DprHseDetailGridData>
  implements RegistrationsComponent<DprHseDetailGridData>, OnChanges
{
  constructor(private dprHseGridService: DprHseGridService) {
    super();
  }

  //#region Lifecycle hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.predefinedChanges?.currentValue) {
      this.gridComponent?.api?.setPredefinedChanges?.(this.predefinedChanges);
    }

    if (changes.canEdit) {
      this.gridOptions$.next(this.getGridOptions());
      this.gridColumns$.next(this.getGridColumns());
    }
  }

  //#endregion

  protected getGridOptions(): GridOptions<DprHseDetailGridData> {
    return {
      sequenceConfig: {
        disabled: true,
      },
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: false,
        keepAutogeneratedIds: true,
      },
      editConfig: { allowEditing: this.canEdit },
      deleteConfig: { allowDeleting: false },
      rowConfig: {
        allowDeleting: false,
        allowSelection: false,
      },
      selectionConfig: { allowSelection: false },
      saveConfig: { isExternalSave: true },
      events: {
        onGridRegistered: this.onGridRegistered.bind(this),
      },
    };
  }

  protected getGridColumns(): GridColumn<DprHseDetailGridData>[] {
    return [
      {
        columnDef: 'name',
        header: {
          name: $localize`:@@dprDetails.dprHse.colCategory:Category`,
        },
        minWidth: 250,
        display: { type: TextDisplayComponent },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowEditing: false,
      },
      {
        columnDef: 'previous',
        minWidth: 80,
        header: {
          name: getStaticMessage(StaticMessage.PreviousColumn),
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        display: { type: TextDisplayComponent },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowEditing: false,
      },
      {
        columnDef: 'today',
        minWidth: 80,
        header: {
          name: getStaticMessage(StaticMessage.TodayColumn),
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        display: { type: TextDisplayComponent },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: false,
              min: 0,
              max: Constants.NumericOptions.intMax,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.todayChanged.bind(this),
        allowEditing: this.canEdit,
      },
      {
        columnDef: 'total',
        minWidth: 80,
        header: {
          name: getStaticMessage(StaticMessage.TotalColumn),
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        display: { type: TextDisplayComponent },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowEditing: false,
      },
      GridColumns.comment,
    ];
  }

  //#region Private methods

  private todayChanged(data: DprHseDetailGridData): void {
    data.total = this.dprHseGridService.calculateTotal(
      data.previous,
      +data.today
    );
  }

  //#endregion
}
